import * as React from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import UploadRoundedIcon from "@mui/icons-material/UploadRounded";
import AdminShell, {
  fieldSx,
  panelSx,
  primaryButtonSx,
  selectMenuProps,
  manageButtonSx,
  tileHeaderSx,
  infoBlockSx,
  sectionTitleSx,
  mutedTextSx,
} from "./AdminShell";
import { getSession } from "../../api/session";
import { slugify } from "../utils/slug";
import { formatPlatform, formatOrbit } from "../missions/platformOrbit";
import { OptionSummary, optionRowSx } from "./payloadOptions";
import {
  adminListAllMissions,
  adminUpsertMission,
  adminDeleteMission,
  fetchMissionPayloads,
  uploadMissionCoverImage,
  fetchAllLaunchOptions,
  fetchMissionLaunchAssignments,
  adminSetLaunchOptionMission,
} from "../../api/queries";
import type { AdminMissionRow, AdminPayloadCategoryRow } from "../../types/admin";
import type { PayloadOptionRow } from "../../types/payload";
import type { LaunchOptionRow } from "../../types/launch";

const PLATFORM_OPTIONS = ["1U", "3U", "6U", "Other"] as const;
const ORBIT_OPTIONS = ["LEO", "MEO", "HEO", "GEO", "Other"] as const;
const CATEGORY_OPTIONS = ["Active Missions", "Upcoming Missions", "Training Missions", "Archived Missions"] as const;

const EMPTY_MISSION = {
  id: null as string | null,
  name: "",
  category: CATEGORY_OPTIONS[0] as string,
  summary: "",
  objective: "",
  // Never shown in the form: kept as-is when editing, derived from the name on create.
  iconKey: "",
  platformType: "6U" as (typeof PLATFORM_OPTIONS)[number],
  orbitType: "LEO" as (typeof ORBIT_OPTIONS)[number],
  platformDetail: "",
  orbitDetail: "",
  imageUrl: "",
  isActive: true,
};

type MissionForm = typeof EMPTY_MISSION;
type SetMissionForm = React.Dispatch<React.SetStateAction<MissionForm>>;

function toMissionForm(m: AdminMissionRow): MissionForm {
  return {
    id: m.id,
    name: m.name,
    category: m.category,
    summary: m.summary,
    objective: m.objective,
    iconKey: m.icon_key,
    platformType: m.platform_type,
    orbitType: m.orbit_type,
    platformDetail: m.platform_detail ?? "",
    orbitDetail: m.orbit_detail ?? "",
    imageUrl: m.image_url ?? "",
    isActive: m.is_active,
  };
}

// A mission saved before the dropdown existed may hold a category outside
// the predefined list -- keep it selectable so editing doesn't silently change it.
function categoryOptionsFor(category: string): readonly string[] {
  const predefined: readonly string[] = CATEGORY_OPTIONS;
  return !category || predefined.includes(category) ? predefined : [...predefined, category];
}

function DetailItem(props: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.25}>
      <Typography sx={{ color: "rgba(226,232,240,0.55)", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
        {props.label}
      </Typography>
      <Typography sx={{ color: "#FFFFFF", fontSize: 13, whiteSpace: "pre-wrap" }}>{props.children}</Typography>
    </Stack>
  );
}

export default function AdminMissions(props: { handlePageChange: (page: string) => void }) {
  const { handlePageChange } = props;
  const session = React.useMemo(() => getSession(), []);
  const email = session?.email ?? "";

  const [missions, setMissions] = React.useState<AdminMissionRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [addForm, setAddForm] = React.useState(EMPTY_MISSION);
  const [showAddForm, setShowAddForm] = React.useState(false);
  const [editForm, setEditForm] = React.useState(EMPTY_MISSION);
  const [savingMission, setSavingMission] = React.useState(false);
  const [showInactive, setShowInactive] = React.useState(false);
  const [uploadingImage, setUploadingImage] = React.useState(false);

  // One tile is open at a time: "view" (clicking the tile) is read-only,
  // "manage" (the Manage button) edits the mission info. Payloads are only
  // listed here; they are managed and assigned on the Payloads tab.
  const [selectedMissionId, setSelectedMissionId] = React.useState<string | null>(null);
  const [mode, setMode] = React.useState<"view" | "manage">("view");
  const [categories, setCategories] = React.useState<AdminPayloadCategoryRow[]>([]);
  const [options, setOptions] = React.useState<PayloadOptionRow[]>([]);
  // All launch options, and which of them the open mission offers. Chosen
  // here per mission, so a bigger or smaller mission only offers rockets
  // that fit it; the options themselves are edited on the Launches tab.
  const [launchOptions, setLaunchOptions] = React.useState<LaunchOptionRow[]>([]);
  const [offeredLaunchIds, setOfferedLaunchIds] = React.useState<string[]>([]);
  const [launchError, setLaunchError] = React.useState<string | null>(null);
  const [togglingLaunchId, setTogglingLaunchId] = React.useState<string | null>(null);

  const loadMissions = React.useCallback(async () => {
    setLoading(true);
    const { data, error: fetchError } = await adminListAllMissions(email);
    setLoading(false);
    if (fetchError) {
      setError(fetchError.message);
      return;
    }
    setMissions(data ?? []);
  }, [email]);

  React.useEffect(() => {
    loadMissions();
  }, [loadMissions]);

  // Loads the payloads offered in this mission, with the types they belong to.
  const loadPayloads = React.useCallback(async (missionId: string) => {
    const { categories: categoryRows, options: optionRows, error: fetchError } = await fetchMissionPayloads(missionId);
    if (fetchError) {
      setError(fetchError.message);
      return;
    }
    setCategories(categoryRows);
    setOptions(optionRows);
  }, []);

  const loadLaunches = React.useCallback(async (missionId: string) => {
    const [{ data: all, error: allError }, { data: assigned, error: assignedError }] = await Promise.all([
      fetchAllLaunchOptions(),
      fetchMissionLaunchAssignments(),
    ]);
    if (allError || assignedError) {
      setLaunchError((allError ?? assignedError)?.message ?? "Couldn't load the launch options.");
      return;
    }
    setLaunchError(null);
    setLaunchOptions(all ?? []);
    setOfferedLaunchIds((assigned ?? []).filter((a) => a.mission_id === missionId).map((a) => a.option_id));
  }, []);

  const toggleLaunch = async (missionId: string, optionId: string, offered: boolean) => {
    setTogglingLaunchId(optionId);
    const { error: toggleError } = await adminSetLaunchOptionMission(email, optionId, missionId, offered);
    setTogglingLaunchId(null);
    if (toggleError) {
      setError(toggleError.message);
      return;
    }
    setOfferedLaunchIds((prev) => (offered ? [...prev, optionId] : prev.filter((id) => id !== optionId)));
  };

  const openMission = (m: AdminMissionRow, nextMode: "view" | "manage") => {
    const closing = selectedMissionId === m.id && mode === nextMode;
    const switching = selectedMissionId !== m.id;
    setSelectedMissionId(closing ? null : m.id);
    setMode(nextMode);
    setError(null);
    setEditForm(closing ? EMPTY_MISSION : toMissionForm(m));
    if (switching) {
      setCategories([]);
      setOptions([]);
      setLaunchOptions([]);
      setOfferedLaunchIds([]);
      loadPayloads(m.id);
      loadLaunches(m.id);
    }
  };

  const saveMission = async (form: MissionForm) => {
    if (!form.name.trim()) {
      setError("Mission name is required.");
      return;
    }
    setSavingMission(true);
    setError(null);

    const { error: saveError } = await adminUpsertMission(email, {
      id: form.id,
      slug: slugify(form.name),
      name: form.name.trim(),
      category: form.category || CATEGORY_OPTIONS[0],
      summary: form.summary.trim(),
      objective: form.objective.trim(),
      iconKey: form.iconKey || slugify(form.name),
      platformType: form.platformType,
      orbitType: form.orbitType,
      platformDetail: form.platformType === "Other" ? form.platformDetail.trim() || null : null,
      orbitDetail: form.orbitType === "Other" ? form.orbitDetail.trim() || null : null,
      imageUrl: form.imageUrl.trim() || null,
      isActive: form.isActive,
    });

    setSavingMission(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    if (form.id === null) {
      setAddForm(EMPTY_MISSION);
      setShowAddForm(false);
    }
    loadMissions();
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, form: MissionForm, setForm: SetMissionForm) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      setError("Only JPEG, JPG, or PNG image files are allowed.");
      return;
    }
    if (!form.name.trim()) {
      setError("Enter the mission name before uploading a cover image.");
      return;
    }

    setUploadingImage(true);
    setError(null);

    const { data: publicUrl, error: uploadError } = await uploadMissionCoverImage(slugify(form.name), file);

    setUploadingImage(false);
    if (uploadError) {
      setError(uploadError.message);
      return;
    }
    setForm((prev) => ({ ...prev, imageUrl: publicUrl ?? "" }));
  };

  const deleteMission = async (id: string) => {
    const { error: delError } = await adminDeleteMission(email, id);
    if (delError) {
      setError(delError.message);
      return;
    }
    if (selectedMissionId === id) setSelectedMissionId(null);
    loadMissions();
  };

  const visibleMissions = React.useMemo(() => {
    return missions
      .filter((m) => showInactive || m.is_active)
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [missions, showInactive]);

  // The mission info fields, shared by the "Add mission" panel and a tile's Manage view.
  const renderMissionFields = (form: MissionForm, setForm: SetMissionForm) => (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
        <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={fieldSx} size="small" />
        <TextField
          select
          label="Category"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          sx={fieldSx}
          size="small"
          SelectProps={{ MenuProps: selectMenuProps }}
        >
          {categoryOptionsFor(form.category).map((c) => (
            <MenuItem key={c} value={c} sx={{ fontSize: 13 }}>
              {c}
            </MenuItem>
          ))}
        </TextField>
        <Stack spacing={1}>
          <TextField
            select
            label="Platform type"
            value={form.platformType}
            onChange={(e) => setForm({ ...form, platformType: e.target.value as any })}
            sx={fieldSx}
            size="small"
            SelectProps={{ MenuProps: selectMenuProps }}
          >
            {PLATFORM_OPTIONS.map((p) => (
              <MenuItem key={p} value={p} sx={{ fontSize: 13 }}>
                {p}
              </MenuItem>
            ))}
          </TextField>
          {form.platformType === "Other" && (
            <TextField
              label="Platform dimensions (cm)"
              placeholder="e.g. 40×40×20 cm"
              value={form.platformDetail}
              onChange={(e) => setForm({ ...form, platformDetail: e.target.value })}
              sx={fieldSx}
              size="small"
            />
          )}
        </Stack>
        <Stack spacing={1}>
          <TextField
            select
            label="Orbit"
            value={form.orbitType}
            onChange={(e) => setForm({ ...form, orbitType: e.target.value as any })}
            sx={fieldSx}
            size="small"
            SelectProps={{ MenuProps: selectMenuProps }}
          >
            {ORBIT_OPTIONS.map((o) => (
              <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          {form.orbitType === "Other" && (
            <TextField
              label="Apogee / perigee"
              placeholder="e.g. 800 km / 700 km"
              value={form.orbitDetail}
              onChange={(e) => setForm({ ...form, orbitDetail: e.target.value })}
              sx={fieldSx}
              size="small"
            />
          )}
        </Stack>
        <TextField label="Summary (Dashboard tile)" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} sx={fieldSx} size="small" multiline />
        <TextField label="Objective (full text)" value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} sx={fieldSx} size="small" multiline />
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              component="label"
              size="small"
              disabled={uploadingImage}
              startIcon={uploadingImage ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : <UploadRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#C4B5FD", border: "1px solid #4C3A70", borderRadius: 1.5, px: 1.25 }}
            >
              {uploadingImage ? "Uploading…" : form.imageUrl ? "Replace cover image" : "Upload cover image"}
              <input type="file" hidden accept="image/jpeg,image/jpg,image/png" onChange={(e) => handleImageUpload(e, form, setForm)} />
            </Button>
            {form.imageUrl && (
              <Box
                component="img"
                src={form.imageUrl}
                sx={{ width: 32, height: 32, borderRadius: 1, objectFit: "cover", border: "1px solid #3E4258" }}
              />
            )}
            {form.imageUrl && (
              <Button
                size="small"
                onClick={() => setForm({ ...form, imageUrl: "" })}
                sx={{ textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "rgba(226,232,240,0.7)" }}
              >
                Remove
              </Button>
            )}
          </Stack>
          <Typography sx={{ color: "rgba(226,232,240,0.5)", fontSize: 10.5 }}>JPEG, JPG or PNG. Enter the mission name first.</Typography>
        </Stack>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={form.isActive}
            onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            sx={{ color: "rgba(196,181,253,0.6)", "&.Mui-checked": { color: "#A78BFA" } }}
          />
        }
        label={<Typography sx={{ fontSize: 13, color: "rgba(226,232,240,0.8)" }}>Active (visible when offered by a workshop)</Typography>}
      />
    </>
  );

  // The payloads offered in the open mission, grouped by type. Read-only:
  // payloads are created, edited and assigned to missions on the Payloads tab.
  const renderMissionPayloads = () => (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" rowGap={1}>
        <Stack spacing={0.25}>
          <Typography sx={sectionTitleSx}>Payloads</Typography>
          <Typography sx={mutedTextSx}>The payloads participants can choose between on this mission's sheet.</Typography>
        </Stack>
        <Button
          size="small"
          onClick={() => handlePageChange("AdminPayloads")}
          sx={{ textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#C4B5FD" }}
        >
          Manage on the Payloads tab
        </Button>
      </Stack>
      {categories.length === 0 && <Typography sx={mutedTextSx}>No payloads assigned to this mission yet.</Typography>}
      {categories.map((c) => (
        <Box key={c.id} sx={{ p: 1.5, borderRadius: 2, border: "1px solid rgba(168,85,247,0.2)" }}>
          <Stack spacing={1}>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 14 }}>
              {c.label}{" "}
              {c.custom_criterion_label && (
                <Box component="span" sx={{ color: "rgba(226,232,240,0.5)", fontWeight: 500, fontSize: 13 }}>— {c.custom_criterion_label}</Box>
              )}
            </Typography>
            {options
              .filter((o) => o.category_id === c.id)
              .map((o) => (
                <Box key={o.id} sx={optionRowSx}>
                  <OptionSummary option={o} customCriterionLabel={c.custom_criterion_label} />
                </Box>
              ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );

  // The launch options this mission offers on its sheet. While managing,
  // every launch option is listed with a checkbox to offer it or not.
  const renderMissionLaunches = (missionId: string, editable: boolean) => {
    const offered = launchOptions.filter((o) => offeredLaunchIds.includes(o.id));
    const shown = editable ? launchOptions : offered;
    return (
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" rowGap={1}>
          <Stack spacing={0.25}>
            <Typography sx={sectionTitleSx}>Launch options ({offered.length} offered)</Typography>
            <Typography sx={mutedTextSx}>
              The rockets participants choose between on this mission's sheet. About three that fit the mission's size and orbit
              work best.
            </Typography>
          </Stack>
          <Button
            size="small"
            onClick={() => handlePageChange("AdminLaunches")}
            sx={{ textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#C4B5FD" }}
          >
            Add or edit on the Launches tab
          </Button>
        </Stack>
        {launchError && <Typography sx={{ color: "#FCA5A5", fontSize: 13 }}>{launchError}</Typography>}
        {!launchError && shown.length === 0 && (
          <Typography sx={mutedTextSx}>
            {editable ? "There are no launch options yet. Create them on the Launches tab." : "No launch options offered in this mission yet."}
          </Typography>
        )}
        {shown.map((o) => {
          const isOffered = offeredLaunchIds.includes(o.id);
          const facts = `${[o.vehicle, o.provider].filter(Boolean).join(" · ")} — ${Math.round(o.orbit_altitude_km)} km, ${o.orbit_inclination_deg}°`;
          return editable ? (
            <FormControlLabel
              key={o.id}
              sx={{ mx: 0, ...optionRowSx }}
              control={
                <Checkbox
                  size="small"
                  checked={isOffered}
                  disabled={togglingLaunchId === o.id}
                  onChange={(e) => toggleLaunch(missionId, o.id, e.target.checked)}
                  sx={{ color: "rgba(226,232,240,0.5)", "&.Mui-checked": { color: "#A78BFA" } }}
                />
              }
              label={
                <Typography sx={{ color: "#FFFFFF", fontSize: 13 }}>
                  {o.name} <Box component="span" sx={{ color: "rgba(226,232,240,0.55)" }}>{facts}</Box>
                </Typography>
              }
            />
          ) : (
            <Box key={o.id} sx={optionRowSx}>
              <Typography sx={{ color: "#FFFFFF", fontSize: 13 }}>
                {o.name} <Box component="span" sx={{ color: "rgba(226,232,240,0.55)" }}>{facts}</Box>
              </Typography>
            </Box>
          );
        })}
      </Stack>
    );
  };

  const renderMissionDetails = (m: AdminMissionRow) => (
    <Stack spacing={2} sx={{ pt: 1.5, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        {m.image_url && (
          <Box
            component="img"
            src={m.image_url}
            sx={{ width: { xs: "100%", sm: 160 }, height: 110, borderRadius: 1.5, objectFit: "cover", border: "1px solid #3E4258", flexShrink: 0 }}
          />
        )}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5, flex: 1 }}>
          <DetailItem label="Category">{m.category}</DetailItem>
          <DetailItem label="Status">{m.is_active ? "Active" : "Inactive"}</DetailItem>
          <DetailItem label="Platform type">
            {m.platform_type === "Other" && m.platform_detail ? m.platform_detail : formatPlatform(m.platform_type)}
          </DetailItem>
          <DetailItem label="Orbit">{m.orbit_type === "Other" && m.orbit_detail ? m.orbit_detail : formatOrbit(m.orbit_type)}</DetailItem>
        </Box>
      </Stack>
      <DetailItem label="Summary">{m.summary || "—"}</DetailItem>
      <DetailItem label="Objective">{m.objective || "—"}</DetailItem>

      {renderMissionPayloads()}
      {renderMissionLaunches(m.id, false)}
    </Stack>
  );

  const renderMissionManage = () => (
    <Stack spacing={2.5} sx={{ pt: 1.5, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
      <Box sx={infoBlockSx}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <EditRoundedIcon sx={{ fontSize: 15, color: "#C4B5FD" }} />
            <Typography sx={{ color: "#E9D5FF", fontWeight: 800, fontSize: 12.5, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Mission info
            </Typography>
          </Stack>
          {renderMissionFields(editForm, setEditForm)}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => saveMission(editForm)} disabled={savingMission} sx={primaryButtonSx()}>
              {savingMission ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Save changes"}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {renderMissionPayloads()}
      {selectedMissionId && renderMissionLaunches(selectedMissionId, true)}
    </Stack>
  );

  return (
    <AdminShell title="Mission management" currentPage="AdminMissions" handlePageChange={handlePageChange}>
      {error && (
        <Alert severity="error" variant="filled" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {!showAddForm ? (
        <Button
          onClick={() => {
            setAddForm(EMPTY_MISSION);
            setShowAddForm(true);
          }}
          startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />}
          sx={primaryButtonSx({ alignSelf: "flex-start" })}
        >
          Add new mission
        </Button>
      ) : (
        <Box sx={panelSx}>
          <Stack spacing={1.5}>
            <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 15 }}>Add mission</Typography>
            {renderMissionFields(addForm, setAddForm)}
            <Stack direction="row" spacing={1}>
              <Button onClick={() => saveMission(addForm)} disabled={savingMission} sx={primaryButtonSx()}>
                {savingMission ? <CircularProgress size={16} sx={{ color: "inherit" }} /> : "Add mission"}
              </Button>
              <Button
                onClick={() => {
                  setAddForm(EMPTY_MISSION);
                  setShowAddForm(false);
                }}
                sx={{ textTransform: "none", fontWeight: 700, color: "rgba(226,232,240,0.7)" }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress sx={{ color: "#C4B5FD" }} />
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => setShowInactive((v) => !v)}
              sx={{ textTransform: "none", fontWeight: 700, fontSize: 12.5, color: "#C4B5FD" }}
            >
              {showInactive ? "Hide inactive missions" : "Show inactive missions"}
            </Button>
          </Stack>
          {visibleMissions.map((m) => {
            const open = selectedMissionId === m.id;
            const managing = open && mode === "manage";
            return (
              <Box key={m.id} sx={panelSx}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    rowGap={1}
                    // While managing, a stray click on the header must not throw away unsaved edits.
                    onClick={managing ? undefined : () => openMission(m, "view")}
                    sx={managing ? undefined : tileHeaderSx}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {open ? (
                        <ExpandMoreRoundedIcon sx={{ fontSize: 20, color: "#C4B5FD" }} />
                      ) : (
                        <ChevronRightRoundedIcon sx={{ fontSize: 20, color: "rgba(226,232,240,0.6)" }} />
                      )}
                      <Stack>
                        <Typography sx={{ color: "#FFFFFF", fontWeight: 800, fontSize: 15 }}>
                          {m.name} {!m.is_active && <Box component="span" sx={{ color: "rgba(226,232,240,0.4)", fontSize: 11, fontWeight: 700 }}>(inactive)</Box>}
                        </Typography>
                        <Typography sx={{ color: "rgba(226,232,240,0.55)", fontSize: 12 }}>{m.slug}</Typography>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Button
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMission(m, "manage");
                        }}
                        startIcon={managing ? <CloseRoundedIcon sx={{ fontSize: 18 }} /> : <EditRoundedIcon sx={{ fontSize: 18 }} />}
                        sx={manageButtonSx(managing)}
                      >
                        {managing ? "Close" : "Manage"}
                      </Button>
                      <IconButton
                        size="small"
                        aria-label="Delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteMission(m.id);
                        }}
                      >
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 17, color: "rgba(248,113,113,0.75)" }} />
                      </IconButton>
                    </Stack>
                  </Stack>

                  {open && (managing ? renderMissionManage() : renderMissionDetails(m))}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </AdminShell>
  );
}
